import React, { Fragment } from 'react';
import {
  Chart as ChartJS,
  registerables
} from 'chart.js';
import { Line as ChartJsLine } from 'react-chartjs-2';
import pluginLegendOnCanvases from './pluginLegendsOnCanvases';
import shortenLabelName from './shortenLabelName';
import './Line.css';
import { isSafari } from 'react-device-detect';

ChartJS.register(...registerables);


const chartPaddingLeft = 20; // y軸ラベルをテーブルの左外側に配置するためのパディング
const tickLengthBottom = 8; // y軸ラベルによる縦方向のパディングを覆うパディング

const options = {
  layout: {
    autoPadding: false, // 左右のパディング削除その1
    padding: {
      left: chartPaddingLeft,
    }
  },
  datasets: {
    line: {
      pointRadius: 8,
      borderWidth: 2.5,
    },
  },
  scales: {
    xAxis: {
      offset: true, // 線の折れ目をセルの中央に持ってくる設定
      ticks: {
        display: false, // x軸ラベルを表示しない
        autoSkip: false,
      },
      grid: {
        drawBorder: false,
        // borderColor: '#000000',
        // borderWidth: 2,
        // color: '#000000',
        offset: true,
        tickWidth: 0, // 目盛を表示しない
        tickLength: tickLengthBottom,  // 縦方向の位置調整
        drawOnChartArea: false,
      }
    },
    yAxis: {
      min: 0,
      // max: 50,
      ticks: {
        stepSize: 10,
        mirror: true, // y軸ラベルによる左右の位置ズレが発生しないようにする
        padding: 0, // 左右のパディング削除その2
        color: '#000000', // 文字色
        showLabelBackdrop: true,  // 軸ラベルが目盛線で隠れないようにする
        backdropColor: 'rgba(255, 255, 255, 0.9)',
        backdropPadding: 2,
      },
      grid: {
        color: '#000000',
        drawBorder: false,
        tickLength: -chartPaddingLeft,  // y軸ラベルをテーブルの外に配置する
        tickWidth: 0,
        // lineWidth: 1.5,
        // borderDash: [4, 2],
        drawOnChartArea: false,
      }
    },
  },
  plugins: {
    legend: {
      display: false,
      labels: {
        usePointStyle: true,
      },
    },
    tooltip: {
      callbacks: {
        label(ctx) {
          const l = ctx.dataset.label;
          const v = ctx.dataset.data[ctx.dataIndex].toFixed(1);
          return `${l}: ${v}`;
        },
      },
    },
  },
};

const plugins = [
  pluginLegendOnCanvases,
];

function Line({ json }) {
  const {
    labelGroups,
    drawLastGroup = true,
    subtotalLabels = [],
    datasets,
    verticalTop = false,
  } = json;

  const labels = labelGroups.flat().map(l => shortenLabelName(l));

  const chartLabelGroups = labelGroups.slice(0, drawLastGroup ? labelGroups.length : -1);
  const chartLabels = [];
  const gridLines = new Set();
  for (const lg of chartLabelGroups) {
    chartLabels.push(...lg);
    gridLines.add(chartLabels.length);
  }
  gridLines.delete(chartLabels.length);
  // const widthFunction = (ctx) => {
  //   if (gridLines.has(ctx.index)) {
  //     return 2;
  //   } else {
  //     return 0;
  //   }
  // };
  // options.scales.xAxis.grid.lineWidth = widthFunction;
  // options.scales.xAxis.grid.tickWidth = (ctx) => widthFunction(ctx) + 2;

  const lineBorderWidth = 3;
  const linePointRadius = 4;
  const styles = [
    {
      pointStyle: 'rect',
      borderColor: 'rgb(234 88 12)',
      pointBackgroundColor: 'rgb(234 88 12)',
      borderWidth: 4,
      pointRadius: 6,
    },
    {
      pointStyle: 'rect',
      borderColor: 'rgb(253 186 116)',
      pointBackgroundColor: 'rgb(253 186 116)',
      borderWidth: lineBorderWidth,
      pointRadius: linePointRadius,
    },
    {
      pointStyle: 'rect',
      borderColor: 'rgb(254 226 226)',
      pointBackgroundColor: 'rgb(254 226 226)',
      borderWidth: lineBorderWidth,
      pointRadius: linePointRadius,
    },
    {
      pointStyle: 'rect',
      borderColor: 'rgb(221 214 254)',
      pointBackgroundColor: 'rgb(221 214 254)',
      borderWidth: lineBorderWidth,
      pointRadius: linePointRadius,
    },
    {
      pointStyle: 'rect',
      borderColor: 'rgb(148 163 184)',
      pointBackgroundColor: 'rgb(148 163 184)',
      borderWidth: lineBorderWidth,
      pointRadius: linePointRadius,
    },
    {
      pointStyle: 'rect',
      borderColor: 'rgb(226 232 240)',
      pointBackgroundColor: 'rgb(226 232 240)',
      borderWidth: lineBorderWidth,
      pointRadius: linePointRadius,
    }
  ];
  const chartDetasets = datasets.map((ds, i) => {
    return {
      label: ds.label,
      data: ds.data.slice(0, chartLabels.length),
      ...styles[i % styles.length],
    };
  });

  const data = {
    labels: chartLabels.map(l => shortenLabelName(l)),
    datasets: chartDetasets
  };

  // 描画のための補助関数群
  const paddingCell = (n) => {
    if (n > 0) {
      return <td colSpan={n}></td>;
    } else {
      return null;
    }
  };
  const preSubtotalCell = (() => {
    if (subtotalLabels.length > 0) {
      return <td className="linechart-pre-subtotal-cell"></td>
    } else {
      return null;
    }
  })();
  const label2verticalNodes = (label) => {
    const maxLen = 20;  // 1列あたりの最大文字数
    let len = 0;
    const nodes = label.split('\n').map((s, i) => {
      len = Math.max(len, s.length);
      return (
        <Fragment key={i}>
          { i > 0 && <br /> }
          {s}
        </Fragment>
      );
    });
    const margin = process.env.REACT_APP_IS_EXTERNAL ? 5 : 0;
    len = Math.min(len + margin, maxLen);
    return <div className="linechart-vertical" style={{height: `${len + 0.5}em`}}>{nodes}</div>
  };
  const notDrawnLabelsNum = labels.length - chartLabels.length;
  const minWidth = 20 + (labels.length + subtotalLabels.length) * 3;

  // 凡例描画のために表を分離
  const legendCvsRefs = [];
  const datasetsRows = [];
  options.plugins[pluginLegendOnCanvases.id] = {
    canvasElementsFunction() {
      return legendCvsRefs.map((r) => r.current);
    },
  };
  datasets.forEach((ds, i) => {
    const legendWidth = 40;
    const ref = React.createRef();
    legendCvsRefs.push(ref);
    datasetsRows.push(
      <tr key={i}>
        <td className="linechart-bordered linechart-side-b linechart-left-align">
          <canvas ref={ref} width={legendWidth} height={legendWidth} className="linechart-legend-canvas"></canvas>
          {shortenLabelName(ds.label)}
        </td>
        <td className="linechart-bordered linechart-side-b
          linechart-right-align
          linechart-num-cell
          linechart-unbold">
          { /* 調査数 */ ds.n.toLocaleString('ja-JP')}
        </td>
        {ds.data.map((v, j) => {
          // 値
          let classes = ['linechart-bordered linechart-side-b linechart-num-cell'];
          if (gridLines.has(j)) {
            // 縦罫線の強調
            classes.push('linechart-emph-left-b');
          }
          if (typeof v !== "string" && v !== 0.0) {
            // v に文字列が入る場合、および0である場合に装飾されないようにする
            let upper = 0;
            for (const x of ds.data) {
              if (v < x) {
                upper++;
              }
            }
            if (upper === 0) {
              classes.push('linechart-highest');
            } else if (upper <= 2) {
              classes.push('linechart-higher');
            }
          }
          return (
            // v に数値だけでなく "*" が入る場合がある
            <td key={j} className={classes.join(' ')}> {typeof v === 'string' ? v : v.toFixed(1)} </td>
          );
        })}
        {preSubtotalCell}
        {subtotalLabels.length > 0 &&
          ds.subtotal.map((v, j) => {
            return (
              // v に数値だけでなく "*" が入る場合がある
              <td key={j} className="linechart-bordered linechart-side-b linechart-num-cell">
                {typeof v === 'string' ? v : v.toFixed(1)}
              </td>
            );
          }
        )}
      </tr>
    );
  });

  // 表側の文字数を計算
  const sideLength = Math.min(
    20,
    1.5 + datasets.reduce((max, cur) => Math.max(max, cur.label.length), 0)
  );

  // 描画
  return (
    <div className="linechart-table-container">
      <table className="linechart-chart-table" style={{minWidth: `${minWidth}rem`}}>
        <tbody>
          <tr>
            <td style={{width: `${1.5 + sideLength}em`}}></td>
            <td className="linechart-n-top-cell"></td>
            <td colSpan={chartLabels.length}
                className="linechart-left-align"
            > (%) </td>
            {paddingCell(notDrawnLabelsNum)}
            {preSubtotalCell}
            {paddingCell(subtotalLabels.length)}
          </tr>
          <tr>
            <td colSpan="2"></td>
            <td colSpan={chartLabels.length} className="linechart-bordered linechart-full-b linechart-canvas-cell">
              <div className="linechart-canvas-container"
                style={{
                  width: `calc(100% + ${chartPaddingLeft}px)`,
                  right: `${chartPaddingLeft}px`,
                  top: `${tickLengthBottom}px`,
                }}
              >
                <ChartJsLine options={options} data={data} plugins={plugins} width={300} height={100}/>
              </div>
            </td>
            {paddingCell(notDrawnLabelsNum)}
            {preSubtotalCell}
            {paddingCell(subtotalLabels.length)}
          </tr>
          <tr>
            <td className="linechart-bordered linechart-bottom-b"></td>
            <td className="linechart-bordered linechart-bottom-b linechart-n-cell"
            >調査数</td>
            {labels.map((l, i) => {
              // ラベル（表頭）
              const classes = ["linechart-bordered linechart-full-b"];
              if (gridLines.has(i)) {
                // 縦罫線の強調
                classes.push('linechart-emph-left-b');
              }
              if (verticalTop && !isSafari) {
                classes.push("linechart-vertical-top-cell");
                l = label2verticalNodes(l);
              }
              return (
                <td key={i} className={classes.join(" ")}> {shortenLabelName(l)} </td>
              );
            })}
            {preSubtotalCell}
            {subtotalLabels.map((sl, i) => {
              // 小計のラベル
              const classes = ["linechart-bordered linechart-full-b"];
              if (verticalTop && !isSafari) {
                classes.push("linechart-vertical-top-cell");
                sl = label2verticalNodes(sl);
              }
              return (
                <td key={i}
                  className={classes.join(" ")}
                > {sl} </td>
              );
            })}
          </tr>
          {datasetsRows}
          <tr>
            <td colSpan="2" className="linechart-bordered linechart-top-b"></td>
            <td colSpan={labels.length}
                className="linechart-bordered linechart-top-b linechart-left-align"
            >
              <span className="linechart-highest">100.0</span> 項目別に最もポイントが高い<br />
              <span className="linechart-higher">100.0</span> 項目別に２〜３番目にポイントが高い
            </td>
            {preSubtotalCell}
            { (subtotalLabels.length > 0) &&
              <td colSpan={subtotalLabels.length}
                className="linechart-bordered linechart-top-b"
              ></td>}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default Line;